import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import DepartmentImage from '../../../assets/image/department/crop-man-writing-notepad.jpg';
import DepartmentImage2 from '../../../assets/image/department/computer-book-reading-planning-ideas-students_1418-84.jpg';

function EducationYouthServices() {

  const [Faq, setFaq] = useState(0);

  // pick up theme colors from existing DOM
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');
  const [bodyColor, setBodyColor] = useState('');

  useEffect(() => {
    if (titleRef.current) setTitleColor(window.getComputedStyle(titleRef.current).color);
    if (bodyRef.current) setBodyColor(window.getComputedStyle(bodyRef.current).color);
  }, []);

  // compact stacked subtitle/description layout to avoid awkward wraps
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35, color: titleColor || '#0f172a' };
  const valText = { fontSize: '0.95rem', lineHeight: 1.6, color: bodyColor || undefined };

  const Drop = [
    {
      Que: "What programs are available for youth development?",
      Answer:
        "Mentorship cohorts, leadership academies, after-school enrichment, summer learning, and service-learning. Programs emphasize academic support, life skills, and safe recreation."
    },
    {
      Que: "How can I enroll my child in educational support programs?",
      Answer:
        "Apply online or at a neighborhood center. You’ll typically need proof of residency, student ID/age, and emergency contacts. Translation and disability accommodations are available."
    },
    {
      Que: "What initiatives support young entrepreneurs?",
      Answer:
        "Entrepreneurship bootcamps, pitch nights, micro-grants, and mentorship with local business partners. Youth learn ideation, budgeting, marketing, and responsible use of capital."
    },
    {
      Que: "Is financial aid guidance available for college or training?",
      Answer:
        "Yes. We provide FAFSA and scholarship navigation, workshops on grants/loans, and one-on-one advising for certificate, two-year, and four-year pathways."
    },
    {
      Que: "Do you offer support for students with disabilities or English learners?",
      Answer:
        "Accessible materials, interpreters, assistive technology referrals, and targeted tutoring. Coordinated plans with families and schools to reduce barriers."
    }
  ];

  return (
    <>
      <section className="department-details-section">
        <div className="container">
          <div className="row">

            {/* Sidebar */}
            <div className="col-12 col-lg-4 col-xl-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="department-details-imgbox">
                <img src={DepartmentImage} className="img-fluid" alt="Education" />
              </div>

              <div className="department-details-content-box">
                <h4 ref={titleRef} className="department-details-title">Education</h4>
                <p style={{ color: titleColor || '#0f172a' }}>
                  We expand access to high-quality learning, mentorship, and career-connected opportunities from middle school
                  through postsecondary. Our priorities: readiness, equity, and strong transitions to college and careers.
                </p>
                <p ref={bodyRef}>
                  We partner with schools, colleges, libraries, and community organizations to deliver tutoring, counseling, digital
                  access, and enrichment. Families get clear guidance on milestones—financial aid, dual enrollment, CTE, and work-based
                  learning—so every student can choose a path that fits.
                </p>
              </div>

              <div className="department-details-box">
                <div className="department-details-policy">
                  <span>
                    Promoting education and leadership for the next generation.
                  </span>
                </div>
                <p>
                  Our work includes out-of-school programs, youth leadership pipelines, educator supports, and well-being resources.
                  We focus on closing opportunity gaps with culturally responsive programming and multilingual outreach.
                </p>
              </div>

              <div className="department-details-benefits-inner-box">
                <div className="row row-gutter-30">
                  <div className="col-lg-6">
                    <div className="department-details-benefits-box-image">
                      <img src={DepartmentImage2} className="img-fluid" alt="Youth Development" />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="department-details-benefits-box">
                      <h3>Our Benefits</h3>
                      <p>
                        Clear pathways, strong supports, and real-world experiences that boost readiness.
                      </p>
                      <ul className="list-unstyled list-style">
                        <li>
                          <Link to="/department/education/gtsu">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            GTS State University
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/education/youth-mentorship">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Youth Development and Mentorship
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/education/after-school-programs">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            After-School and Summer Programs
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/education/special-education-support">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Special Education and Learning Support Services
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Outcomes section */}
              <div className="department-details-content-box" style={{ marginTop: '10px' }}>
                <h4
                  className="department-details-title"
                  style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}
                >
                  Outcomes we aim for
                </h4>
                <p style={{ color: bodyColor || undefined, fontSize: '0.95rem' }}>
                  Higher graduation and FAFSA completion rates, increased dual-enrollment and industry credential attainment,
                  expanded paid internships and apprenticeships, and smoother transitions into college, training, or quality jobs.
                </p>
              </div>

              {/* What We Do — compact stacked layout */}
              <div className="department-details-benefits-box">
                <h3>What We Do</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>College & Career Readiness</div>
                      <div style={valText}>
                        Academic advising, tutoring, SAT/ACT prep, dual-enrollment guidance, and individualized graduation plans.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Financial Aid & Scholarships Navigation</div>
                      <div style={valText}>
                        FAFSA completion support, scholarship searches, award letter comparisons, and responsible borrowing education.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>CTE, Internships & Apprenticeships</div>
                      <div style={valText}>
                        Work-based learning, career pathways with local employers, and certifications aligned to in-demand fields.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Digital Learning & Access</div>
                      <div style={valText}>
                        Device lending, connectivity support, and training in study skills, productivity apps, and safe online practices.
                      </div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Youth Well-Being & Belonging</div>
                      <div style={valText}>
                        Counseling referrals, peer mentoring, inclusive clubs, and programs that build leadership and civic engagement.
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Who We Serve */}
              <div className="department-details-benefits-box">
                <h3>Who We Serve</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Students & Families</div>
                      <div style={valText}>Guidance from middle school through postsecondary, with multilingual supports.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Educators & Counselors</div>
                      <div style={valText}>Toolkits, training, and data to personalize support and track milestones.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon}/>
                    <div style={kv}>
                      <div style={keyText}>Community & Employer Partners</div>
                      <div style={valText}>Placement pipelines, mentorship networks, and project-based learning opportunities.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* How to Get Connected */}
              <div className="department-details-content-box">
                <h4 className="department-details-title" style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}>
                  How to Get Connected
                </h4>
                <ol style={{ paddingLeft: '18px', color: bodyColor || undefined, fontSize: '0.95rem', lineHeight: 1.6 }}>
                  <li>Create a student/family profile online or visit a community hub.</li>
                  <li>Select interests: tutoring, mentorship, internships, college advising, or financial aid help.</li>
                  <li>Upload optional documents (transcripts, resume) to match with programs faster.</li>
                  <li>Get a personalized plan with upcoming workshops, deadlines, and contacts.</li>
                </ol>
              </div>

              <div className="accordian-box">
                {Drop.map((items, index) => (
                  <div className={`accordian-box-item ${Faq !== index && "active"}`} key={index}>
                    <div className="accordian-title">
                      <h5>{items.Que}</h5>
                      <i className="fa-solid fa-angle-right" onClick={() => setFaq(index)} />
                    </div>

                    {Faq === index && (
                      <div className="accordian-content" style={{ display: "block" }}>
                        <p>{items.Answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

export default EducationYouthServices;
